import { Dispatch, FC, RefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';

import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { defaultStartTime, searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';

import BulkExportsForm from './components/BulkExportsForm';
import BulkExportsTable from './components/BulkExportsTable';
import BulkExportsModal from './components/BulkExportsModal';
import {
  AddJobData,
  AddJobVariables,
  CancelJobVariables,
  ExportJobFormData,
  ExportJobResponse,
  ExportJobsList,
  ExportJobTableData,
  JobOutput,
  JobStatus,
} from './types';
import { CANCEL_JOB_MUTATION, EXPORT_JOB_MUTATION, JOB_EXPORTS_QUERY } from './queries';
import { timestampToDate } from './utils';

import './BulkExportsPage.scss';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface BulkExportsProps {}

const formInitialState = {
  timeRangeStart: '',
  timeRangeEnd: '',
  query: '',
  label: '',
  s3Bucket: '',
  filenamePrefix: '',
  recipients: [],
};

function useExportFormState(): [ExportJobFormData, Dispatch<SetStateAction<ExportJobFormData>>] {
  const [formState, setFormState] = useState<ExportJobFormData>(formInitialState);

  // Initialize form with the values from URL params
  const searchParams = useRecoilValue(searchParamRecordState);
  const defaultStart = useRecoilValue(defaultStartTime);
  useEffect(() => {
    const syncedFormState = { ...formInitialState };

    syncedFormState.timeRangeStart = searchParams[K.URL_PARAM.START_TIME] || defaultStart;
    syncedFormState.timeRangeEnd =  searchParams[K.URL_PARAM.END_TIME] || K.DEFAULT_SEARCH_END;
    syncedFormState.query = searchParams[K.URL_PARAM.FILTER] || '';

    setFormState(syncedFormState);
    // TODO: Check whether adding the dependencies will have any effect on the behavior
    // eslint-disable-next-line
  }, []);

  return [formState, setFormState];
}

/**
 * This page is used for exporting and viewing job logs to Amazon S3
 */
const BulkExportsPage: FC<BulkExportsProps> = () => {
  const [currentItem, setCurrentItem] = useState<ExportJobTableData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formState, setFormState] = useExportFormState();
  const refElement = useRef<HTMLSpanElement>(null);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(true);
  const timezone = useRecoilValue(timezoneState);

  const showSuccessAlert = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
      duration: 1.5,
    });
  };
  const showErrorAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.ERROR, autoHide: true });
  };

  const { data: tableData, loading } = useQuery<{ bulkExport: ExportJobsList }>(JOB_EXPORTS_QUERY, {
    onError: (error) => {
      showErrorAlert(error.message);
    },
    pollInterval: 3000,
  });

  const [addJob] = useMutation<{ addJob: { id: string } }, AddJobVariables>(EXPORT_JOB_MUTATION, {
    refetchQueries: [
      {
        query: JOB_EXPORTS_QUERY,
      },
    ],
    onCompleted: () => {
      showSuccessAlert('Export Started Successfully');
      setSubmitButtonEnabled(true);
    },
    onError: (error) => {
      showErrorAlert(error.message);
      setSubmitButtonEnabled(true);
    },
  });
  const [cancelJob] = useMutation<{ cancelJob: { id: string } }, CancelJobVariables>(CANCEL_JOB_MUTATION, {
    refetchQueries: [
      {
        query: JOB_EXPORTS_QUERY,
      },
    ],
    onCompleted: () => {
      showSuccessAlert('Job Canceled');
    },
    onError: (error) => {
      showErrorAlert(error.message);
    },
  });

  const scrollToElement = (ref: RefObject<HTMLElement>): void => {
    ref?.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const handleClone = (exportData: ExportJobFormData): void => {
    setFormState(exportData);
    scrollToElement(refElement);
  };

  const handleModalOpen = (item: ExportJobTableData): void => {
    setCurrentItem(item);
    setShowModal(true);
  };

  const handleModalClose = (): void => {
    setShowModal(false);
  };

  const exportsData: ExportJobResponse[] = tableData ? tableData.bulkExport.listJobs : [];

  const jobExports: ExportJobTableData[] = exportsData.map((exportItem) => {
    const timeRangeStart = timestampToDate(exportItem.timeRange.min, timezone);
    const timeRangeEnd = timestampToDate(exportItem.timeRange.max, timezone);
    const createdTimestamp = exportItem.createdTimestamp;
    const status = JobStatus[exportItem.status];
    const output = JobOutput[exportItem.output];

    return {
      id: exportItem.id,
      query: exportItem.query,
      label: exportItem.label,
      // TODO: Wrap the operators in another set of () based on the correct precedence
      // eslint-disable-next-line
      completionPercent: exportItem.completionPercent && (exportItem.completionPercent * 100) || 0,
      createdBy: exportItem.createdBy,
      s3Bucket: exportItem.s3Bucket,
      filenamePrefix: exportItem.filenamePrefix,
      recipients: exportItem.recipients,
      timeRangeStart,
      timeRangeEnd,
      createdTimestamp,
      status,
      output,
    };
  });

  const onFormSubmition = (vars: AddJobData): void => {
    setSubmitButtonEnabled(false);
    addJob({ variables: { job: vars } });
  };

  return (
    <Page docTitle="Batch Export to S3" pageModifier="BulkExports__PageContainer" testId={ELEMENT.bulkExportsPage}>
      <span ref={refElement} />
      <PageHeader title="Export Logs to Amazon S3" helpLink={HELP_ROUTE.HELP_EXPORTS} />
      <BulkExportsForm formData={formState} onSubmit={onFormSubmition} submitButtonEnabled={submitButtonEnabled} />
      <BulkExportsTable title="Recent Exports" data={jobExports} loading={loading} onRowClick={handleModalOpen} />
      <BulkExportsModal
        showModal={showModal}
        onClose={handleModalClose}
        onCancel={(id: string): void => {
          cancelJob({ variables: { jobId: id } });
        }}
        onClone={handleClone}
        currentItem={currentItem}
      />
    </Page>
  );
};
export default BulkExportsPage;
